import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2, Plus, RefreshCw, ListTodo, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { toast } from "sonner";

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
];

const FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "due_today", label: "Due today" },
  { value: "overdue", label: "Overdue" },
  { value: "high", label: "High priority" },
  { value: "completed", label: "Completed" },
];

const fmtDate = (v) => {
  if (!v) return null;
  try {
    return new Date(v).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const toInputDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toIsoLocal9am = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T09:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const PRIO_PILL = {
  high: "bg-red-50 text-red-700 border-red-200",
  normal: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-gray-100 text-gray-600 border-gray-200",
};

const EMPTY_FORM = {
  title: "",
  description: "",
  due_at: "",
  priority: "normal",
};

const isOverdue = (t) => {
  if (t.completed || !t.due_at) return false;
  return new Date(t.due_at).getTime() < Date.now();
};
const isDueToday = (t) => {
  if (t.completed || !t.due_at) return false;
  const due = new Date(t.due_at);
  const now = new Date();
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
};

export const AdminTasks = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("open");
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/tasks");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load tasks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        due_at: toIsoLocal9am(form.due_at),
        priority: form.priority,
      };
      await api.post("/admin/tasks", payload);
      toast.success("Task created.");
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create task.");
    } finally {
      setCreating(false);
    }
  };

  const toggleComplete = async (t) => {
    // Optimistic flip; revert on failure.
    setList((prev) =>
      prev.map((x) =>
        x.id === t.id
          ? { ...x, completed: !t.completed, completed_at: !t.completed ? new Date().toISOString() : null }
          : x,
      ),
    );
    try {
      await api.patch(`/admin/tasks/${t.id}`, { completed: !t.completed });
    } catch (err) {
      // Roll back
      setList((prev) => prev.map((x) => (x.id === t.id ? t : x)));
      toast.error(err?.response?.data?.detail || "Couldn't update task.");
    }
  };

  const setPriority = async (t, value) => {
    setList((prev) => prev.map((x) => (x.id === t.id ? { ...x, priority: value } : x)));
    try {
      await api.patch(`/admin/tasks/${t.id}`, { priority: value });
    } catch (err) {
      setList((prev) => prev.map((x) => (x.id === t.id ? t : x)));
      toast.error(err?.response?.data?.detail || "Couldn't update priority.");
    }
  };

  const setDue = async (t, dateStr) => {
    const iso = toIsoLocal9am(dateStr);
    setList((prev) => prev.map((x) => (x.id === t.id ? { ...x, due_at: iso } : x)));
    try {
      await api.patch(`/admin/tasks/${t.id}`, { due_at: iso });
    } catch (err) {
      setList((prev) => prev.map((x) => (x.id === t.id ? t : x)));
      toast.error(err?.response?.data?.detail || "Couldn't update due date.");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/tasks/${t.id}`);
      setList((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Task deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete task.");
    }
  };

  const filtered = useMemo(() => {
    return list.filter((t) => {
      if (filter === "all") return true;
      if (filter === "open") return !t.completed;
      if (filter === "completed") return !!t.completed;
      if (filter === "high") return t.priority === "high" && !t.completed;
      if (filter === "due_today") return isDueToday(t);
      if (filter === "overdue") return isOverdue(t);
      return true;
    });
  }, [list, filter]);

  return (
    <section
      className="mt-10 rounded-2xl bg-white border border-gray-200 overflow-hidden"
      data-testid="admin-tasks-section"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <ListTodo className="w-4 h-4 text-emerald-700" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-gray-900">
              Internal Tasks
            </h2>
            <p className="text-xs text-gray-500">
              Private admin-only todos. Linked tasks show the related business.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="admin-tasks-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </header>

      <form
        onSubmit={submit}
        className="px-5 py-4 border-b border-gray-100 grid gap-3 sm:grid-cols-12 items-end"
        data-testid="admin-task-form"
      >
        <div className="sm:col-span-4">
          <Label htmlFor="task-title">Title *</Label>
          <Input
            id="task-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Call ACME re: trial extension"
            maxLength={140}
            required
            className="mt-1.5 h-10"
            data-testid="admin-task-title-input"
          />
        </div>
        <div className="sm:col-span-4">
          <Label htmlFor="task-desc">Description</Label>
          <Input
            id="task-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Optional notes"
            maxLength={1000}
            className="mt-1.5 h-10"
            data-testid="admin-task-desc-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="task-due">Due date</Label>
          <input
            id="task-due"
            type="date"
            value={form.due_at}
            onChange={(e) => setForm({ ...form, due_at: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-gray-200 px-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
            data-testid="admin-task-due-input"
          />
        </div>
        <div className="sm:col-span-1">
          <Label>Priority</Label>
          <Select
            value={form.priority}
            onValueChange={(v) => setForm({ ...form, priority: v })}
          >
            <SelectTrigger className="mt-1.5 h-10" data-testid="admin-task-priority-input">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-1">
          <Button
            type="submit"
            disabled={creating}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-10"
            data-testid="admin-task-create"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </form>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap" data-testid="admin-tasks-filters">
        {FILTERS.map((f) => {
          const count =
            f.value === "all"
              ? list.length
              : list.filter((t) => {
                  if (f.value === "open") return !t.completed;
                  if (f.value === "completed") return !!t.completed;
                  if (f.value === "high") return t.priority === "high" && !t.completed;
                  if (f.value === "due_today") return isDueToday(t);
                  if (f.value === "overdue") return isOverdue(t);
                  return false;
                }).length;
          const active = filter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${
                active
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-gray-600 border-gray-200 hover:border-emerald-200 hover:text-emerald-800"
              }`}
              data-testid={`admin-tasks-filter-${f.value}`}
            >
              {f.label}
              <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${active ? "bg-white/20" : "bg-gray-100 text-gray-700"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="p-5">
        {error ? (
          <div className="px-5 py-10 text-center text-red-600" data-testid="admin-tasks-error">
            {error}
          </div>
        ) : loading && list.length === 0 ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 rounded-lg border border-gray-100 bg-gray-50 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-5 py-10 text-center text-gray-500"
            data-testid="admin-tasks-empty"
          >
            {list.length === 0
              ? "No tasks yet. Add one above to start tracking follow-ups."
              : "No tasks match this filter."}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="admin-tasks-list">
            {filtered.map((t) => {
              const overdue = isOverdue(t);
              const due_today = isDueToday(t);
              const accent = t.completed
                ? "border-gray-200 bg-gray-50/60"
                : t.priority === "high"
                ? "border-red-200 bg-red-50/30"
                : overdue
                ? "border-red-200 bg-red-50/40"
                : due_today
                ? "border-amber-200 bg-amber-50/30"
                : "border-gray-200 bg-white";
              return (
                <li
                  key={t.id}
                  className={`rounded-lg border ${accent} p-3 flex items-start gap-3`}
                  data-testid={`admin-task-row-${t.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!t.completed}
                    onChange={() => toggleComplete(t)}
                    className="mt-1 w-4 h-4 accent-emerald-700 shrink-0"
                    data-testid={`admin-task-toggle-${t.id}`}
                    aria-label={t.completed ? "Mark task open" : "Mark task complete"}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`font-medium text-[14px] ${t.completed ? "line-through text-gray-500" : "text-gray-900"}`}
                      >
                        {t.title}
                      </span>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${PRIO_PILL[t.priority] || PRIO_PILL.normal}`}
                      >
                        {t.priority}
                      </span>
                      {overdue ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-red-50 text-red-700 border-red-100">
                          Overdue
                        </span>
                      ) : due_today ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-800 border-amber-100">
                          Due today
                        </span>
                      ) : null}
                      {t.related_business_name ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-800 border-emerald-100">
                          {t.related_business_name}
                        </span>
                      ) : null}
                    </div>
                    {t.description ? (
                      <p className={`mt-0.5 text-[13px] ${t.completed ? "text-gray-400 line-through" : "text-gray-600"}`}>
                        {t.description}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select
                      value={t.priority}
                      onValueChange={(v) => setPriority(t, v)}
                    >
                      <SelectTrigger className="h-8 text-[12px] w-[110px]" data-testid={`admin-task-priority-${t.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <input
                      type="date"
                      value={toInputDate(t.due_at)}
                      onChange={(e) => setDue(t, e.target.value)}
                      className="h-8 rounded-md border border-gray-200 px-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
                      data-testid={`admin-task-due-${t.id}`}
                      aria-label="Due date"
                    />
                    <button
                      type="button"
                      onClick={() => remove(t)}
                      className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete task"
                      data-testid={`admin-task-delete-${t.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

// Hint about completed at usage (kept side-by-side with the component so
// future devs see the API shape without grepping):
//   `completed_at` is server-stamped on transition to completed=true and
//   cleared on transition to completed=false; we just read it for display.
export const _fmtDateExport = fmtDate; // exported for future tests if needed.
